import { Alert, Loader, Stack, Switch, Table, Text } from '@mantine/core'
import { showNotification } from '@mantine/notifications'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useOrpc } from '@/hooks/useClient'
import { QUEUE_COLOR, SANS } from './queueTheme'

type BoardPrefs = { boardId: string; boardName: string; email: boolean; sms: boolean }
type PrefsList = { boards: BoardPrefs[] }

const SAVE_SCOPE = 'board-notify-prefs-save'

// The signed-in user's own choice of task-assignment alerts for EVERY board in
// their org (PD-1809), in one table. Email and SMS switch independently per
// board; the in-app notification always fires. Boards never set show both on.
export function BoardNotificationSettingsModal() {
  const orpc = useOrpc()
  const queryClient = useQueryClient()
  const key = orpc.boardNotifyPrefsList.queryKey({ input: {} })

  const { data, isLoading, isError } = useQuery(
    orpc.boardNotifyPrefsList.queryOptions({ input: {} }),
  )

  const setPrefs = useMutation(
    orpc.boardNotifyPrefsSet.mutationOptions({
      // One save at a time, in click order. Each save sends a board's whole
      // email+SMS pair, so two in flight could land out of order and the earlier
      // click would overwrite the later one — re-enabling a channel the user just
      // turned off. Mutations sharing a scope run serially.
      scope: { id: SAVE_SCOPE },
      // Optimistic so the switch moves at once; rolled back if the save fails.
      onMutate: async (vars: { boardId: string; email: boolean; sms: boolean }) => {
        await queryClient.cancelQueries({ queryKey: key })
        const previous = queryClient.getQueryData<PrefsList>(key)
        if (previous) {
          queryClient.setQueryData<PrefsList>(key, {
            boards: previous.boards.map((b) =>
              b.boardId === vars.boardId ? { ...b, email: vars.email, sms: vars.sms } : b,
            ),
          })
        }
        return { previous }
      },
      onError: (error, _vars, ctx) => {
        if (ctx?.previous) queryClient.setQueryData(key, ctx.previous)
        showNotification({
          color: 'red',
          title: 'Failed to update notification settings',
          message: error instanceof Error ? error.message : 'Unknown error',
        })
      },
      // Refetch only after the last queued save: refetching between them would
      // briefly show a toggle that is still waiting its turn as undone.
      onSettled: () => {
        const pending = queryClient.isMutating({
          predicate: (m) => m.options.scope?.id === SAVE_SCOPE,
        })
        if (pending <= 1) return queryClient.invalidateQueries({ queryKey: key })
      },
    }),
  )

  if (isLoading) {
    return (
      <div className="flex justify-center py-6">
        <Loader size="sm" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <Alert color="red" variant="light">
        Couldn’t load notification settings. Please close and try again.
      </Alert>
    )
  }

  // Always send both, from the row's current (possibly optimistic) state, so a
  // quick second toggle on the same board builds on the first, not reverts it.
  const save = (row: BoardPrefs, change: Partial<Pick<BoardPrefs, 'email' | 'sms'>>) =>
    setPrefs.mutate({ boardId: row.boardId, email: row.email, sms: row.sms, ...change })

  return (
    <Stack gap="md" style={{ fontFamily: SANS }}>
      <Text size="sm" style={{ color: QUEUE_COLOR.subtle }}>
        When a task on a board is assigned to you, also send:
      </Text>
      {data.boards.length === 0 ? (
        <Text size="sm" c="dimmed">
          There are no boards yet.
        </Text>
      ) : (
        <Table verticalSpacing="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Board</Table.Th>
              <Table.Th w={80}>Email</Table.Th>
              <Table.Th w={80}>SMS</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {data.boards.map((row) => (
              <Table.Tr key={row.boardId}>
                <Table.Td>{row.boardName}</Table.Td>
                <Table.Td>
                  <Switch
                    aria-label={`Email for ${row.boardName}`}
                    checked={row.email}
                    onChange={(e) => save(row, { email: e.currentTarget.checked })}
                  />
                </Table.Td>
                <Table.Td>
                  <Switch
                    aria-label={`SMS for ${row.boardName}`}
                    checked={row.sms}
                    onChange={(e) => save(row, { sms: e.currentTarget.checked })}
                  />
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      )}
      <Text size="xs" c="dimmed">
        Texts go to the phone number on your profile. In-app notifications are always on.
      </Text>
    </Stack>
  )
}
